/**
 * Runs page — the system of record for autonomous agent work.
 *
 * This is not a bookmark list of GitLab links: it is the surface that
 * answers "what are my agents doing, under what identity, at what cost,"
 * which is also the governance view an assessor asks for. `useRuns` is
 * already scoped server-side to the caller's own runs (see
 * `router.list_runs`); this page must not attempt to widen or re-filter
 * that.
 *
 * `RunResponse.project_path` is derived server-side (GitLab's
 * `path_with_namespace`, e.g. "group/proj") from `project_id` -- Sanctum's
 * internal project UUID -- so the "Project" column shows that path,
 * falling back to a truncated id only when the project/server could not
 * be resolved (see `agentRun.types.ts` docblock). It links to the
 * project's page, home of its Agent runs panel.
 *
 * The header filters the list to one project with the launcher's project
 * picker. The filter is server-side (`?project_id=`), so the list is still
 * "the newest of mine", now in that project. With a GitLab project picked
 * it also links to that project's page.
 */

import { useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { RunStatusBadge, useRuns, runStartLabel } from '@features/agent_runs';
import { ProjectSelector } from '@features/workspace';
import { useGitServers } from '@features/git/hooks/useGitServers';
import type { GitProject } from '@features/git/types/git.types';
import { Toolbar } from '@components/ui/Toolbar';
import { ROUTES } from '@utils/constants';

function RunsFilter(props: { project: GitProject | null; onSelect: (p: GitProject | null) => void }): ReactElement {
  const { project, onSelect } = props;
  const { data: servers } = useGitServers();
  // Agent runs exist only on GitLab: the project page answers "not found" for any other server.
  const onGitLab =
    project !== null && servers?.servers.find((s) => s.id === project.server_id)?.provider_type === 'gitlab';
  return (
    <Toolbar
      info={<>
        <div className="app-toolbar__select app-toolbar__select--grow">
          <ProjectSelector selectedProject={project} onSelect={onSelect} />
        </div>
        {project !== null && onGitLab && (
          <Link to={`${ROUTES.PROJECT}/${project.id}`} className="app-toolbar__link" title={`Agent runs for ${project.path}`}>
            Project page
          </Link>
        )}
        {project !== null && (
          <button type="button" className="secondary-button" onClick={() => { onSelect(null); }}>
            All projects
          </button>
        )}
      </>}
    />
  );
}

export function RunsPage(): ReactElement {
  const [project, setProject] = useState<GitProject | null>(null);
  const { data: runs, isLoading, error } = useRuns(project?.id ?? null);

  return (
    <div className="runs-page">
      <RunsFilter project={project} onSelect={setProject} />
      <div className="runs-page__container">
        <p className="runs-page__subtitle">
          Autonomous agent executions you have requested, with their identity and status.
        </p>

        {isLoading && <div className="loading-message">Loading runs...</div>}
        {error && <div className="error-message">{error.message}</div>}

        {!isLoading && !error && (!runs || runs.length === 0) && (
          <p className="runs-page__empty">
            {project === null ? 'No agent runs yet.' : `No agent runs of yours in ${project.path} yet.`}
          </p>
        )}

        {runs && runs.length > 0 && (
          <table className="feature-table__table runs-page__table">
            <thead>
              <tr>
                <th>Kind</th>
                <th>Project</th>
                <th>Ref</th>
                <th>Status</th>
                <th>Started</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr key={run.id}>
                  <td>
                    <Link to={`${ROUTES.RUNS}/${run.id}`} className="runs-page__link">
                      {run.kind}
                    </Link>
                  </td>
                  <td className="runs-page__project" title={run.project_id}>
                    <Link to={`${ROUTES.PROJECT}/${run.project_id}`} className="runs-page__link">
                      {run.project_path ?? run.project_id.slice(0, 8)}
                    </Link>
                  </td>
                  <td>{run.ref}</td>
                  <td>
                    <RunStatusBadge status={run.status} />
                  </td>
                  <td className="runs-page__started">{runStartLabel(run)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
